import { faFileAlt, faPen, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { applicationApi } from '../api';
import { ApplicationApiAxiosParamCreator, MyApplicationDetail } from '../api/openapi';
import ApplicationCommentsCreate from '../components/applications/ApplicationCommentsCreate';
import ApplicationEducationEdit from '../components/applications/ApplicationEducationEdit';
import ApplicationFilesUploadEdit from '../components/applications/ApplicationFilesUploadEdit';
import ApplicationINNAEducation from '../components/applications/ApplicationINNAEducation';
import ApplicationJobsEdit from '../components/applications/ApplicationJobsEdit';
import ApplicationOtherEdit from '../components/applications/ApplicationOtherEdit';
import ApplicationPersonEdit from '../components/applications/ApplicationPersonEdit';

const ApplicationView: React.FC = () => {
  const { t } = useTranslation(['viewApplication', 'common']);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [data, setData] = useState<MyApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showPersonEdit, setShowPersonEdit] = useState<boolean>(false);
  const [showEducationEdit, setShowEducationEdit] = useState<boolean>(false);
  const [showJobsEdit, setShowJobsEdit] = useState<boolean>(false);
  const [showOtherEdit, setShowOtherEdit] = useState<boolean>(false);
  const [showFilesEdit, setShowFilesEdit] = useState<boolean>(false);

  const [hasJobError, setHasJobError] = useState(false);
  const [hasEducationError, setHasEducationError] = useState(false);
  const [hasPensionFundFileError, hasHasPensionFundFileError] = useState(false);

  const onHandleEditPerson = () => {
    if (showPersonEdit) {
      setShowPersonEdit(false);
    } else {
      setShowPersonEdit(true);
    }
  };

  const onHandleEditFiles = () => {
    if (showFilesEdit) {
      setShowFilesEdit(false);
    } else {
      setShowFilesEdit(true);
    }
  };

  const onHandleEditEducation = () => {
    if (showEducationEdit) {
      setShowEducationEdit(false);
    } else {
      setShowEducationEdit(true);
    }
  };

  const onHandleEditJobs = () => {
    if (showJobsEdit) {
      setShowJobsEdit(false);
    } else {
      setShowJobsEdit(true);
    }
  };

  const onHandleEditOther = () => {
    if (showOtherEdit) {
      setShowOtherEdit(false);
    } else {
      setShowOtherEdit(true);
    }
  };

  const getApplication = useCallback(() => {
    setLoading(true);

    applicationApi
      .myApplicationRetrieve({
        uuid: id,
      })
      .then((response) => {
        setData(response.data);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const onDownloadFile = async (fileId: string) => {
    const link = await ApplicationApiAxiosParamCreator().myApplicationFileDownload(id, fileId);

    window.location.href = link.url;
  };

  useEffect(() => {
    getApplication();
  }, [getApplication]);

  const onSubmit = async () => {
    setHasJobError(false);
    setHasEducationError(false);
    hasHasPensionFundFileError(false);

    if (data) {
      let hasError = false;

      if (data.job.length === 0) {
        setHasJobError(true);
        hasError = true;
      }

      if (
        data.isMatriculationFinished == null ||
        data.isEducationAbroadFinished == null ||
        data.isJourneymanFinished == null ||
        data.isContractFinished == null ||
        data.hasOngoingContract == null
      ) {
        setHasEducationError(true);
        hasError = true;
      }

      if (data.industry.requiresPensionfundFile && data.files.filter((f) => f.isPensionfundFile).length === 0) {
        hasHasPensionFundFileError(true);
        hasError = true;
      }

      if (!hasError) {
        try {
          setLoading(true);
          await applicationApi.applicationMyApplicationComplete({
            uuid: id,
          });
          history.push('/');
          toast.success(t('applicationSent'));
        } catch (e) {
          toast.error(t('applicationError'));
        }
      } else {
        setLoading(false);
        toast.error(t('notComplete'));
      }
    }
  };

  const onResubmit = async () => {
    setHasJobError(false);
    setHasEducationError(false);
    hasHasPensionFundFileError(false);

    if (data) {
      let hasError = false;

      if (data.job.length === 0) {
        setHasJobError(true);
        hasError = true;
      }

      if (
        data.isMatriculationFinished == null ||
        data.isEducationAbroadFinished == null ||
        data.isJourneymanFinished == null ||
        data.isContractFinished == null ||
        data.hasOngoingContract == null
      ) {
        setHasEducationError(true);
        hasError = true;
      }

      if (data.industry.requiresPensionfundFile && data.files.filter((f) => f.isPensionfundFile).length === 0) {
        hasHasPensionFundFileError(true);
        hasError = true;
      }

      if (!hasError) {
        try {
          setLoading(true);
          await applicationApi.applicationMyApplicationResubmit({
            uuid: id,
          });
          history.push('/');
          toast.success(t('applicationSent'));
        } catch (e) {
          toast.error(t('applicationError'));
        }
      } else {
        setLoading(false);
        toast.error(t('notComplete'));
      }
    }
  };

  const onCancel = async () => {
    // eslint-disable-next-line no-alert
    const conf = window.confirm(`${t('areYouSureCancel')}`);

    if (conf) {
      applicationApi
        .applicationMyApplicationCancel({
          uuid: id,
        })
        .catch(() => {
          toast.error(t('errorCancelling'));
        })
        .finally(() => {
          history.push('/');
          toast.success(t('successCancelling'));
        });
    }
  };

  const onActivate = async () => {
    // eslint-disable-next-line no-alert
    const conf = window.confirm(`${t('areYouSureActivate')}`);
    if (conf) {
      applicationApi
        .applicationMyApplicationActivate({
          uuid: id,
        })
        .catch(() => {
          toast.error(t('errorActivating'));
        })
        .finally(() => {
          toast.success(t('successActivating'));
          getApplication();
        });
    }
  };

  if (error) {
    return <>{t('common:kerfisvilla')}</>;
  }

  if (loading || data == null) {
    return (
      <>
        <FontAwesomeIcon spin icon={faSpinner} />
      </>
    );
  }

  return (
    <>
      {/* Skráning */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('registration')}</h4>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <p className="small text-muted">{t('registrationHelp')}</p>
            </Col>
            <Col md="8">
              <p className="textBold">{t('industry')}</p>
              <p>{data.industry.name}</p>
              <p className="textBold">{t('school')}</p>
              <p>{data.school.name}</p>
              <Form.Check
                custom
                id="iHaveReadTheInformation-yes"
                name="iHaveReadTheInformation"
                inline
                disabled
                label={t('iHaveReadTheInformation')}
                type="checkbox"
                checked
              />{' '}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/* Persónuupplýsingar */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('applicant')}</h4>
            </Col>
            {(data.status === 'DRAFT' || data.status === 'RETURNED') && (
              <Col>
                {showPersonEdit ? (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('close')}</Tooltip>}>
                    <Button className="float-right" variant="danger" onClick={() => onHandleEditPerson()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('editInformation')}</Tooltip>}>
                    <Button className="float-right" variant="primary" onClick={() => onHandleEditPerson()}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Col>
            )}
          </Row>
          {showPersonEdit ? (
            <ApplicationPersonEdit
              item={data}
              onSaveSuccess={() => {
                onHandleEditPerson();
                getApplication();
              }}
            />
          ) : (
            <Row>
              <Col md="4">
                <p className="small text-muted">{t('applicantHelp')}</p>
              </Col>
              <Col>
                <Row>
                  <Col>
                    <p className="textBold">{t('name')}:</p>
                    <p>{data.user.name}</p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('ssn')}:</p>
                    <p>{data.user.username}</p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('address')}:</p>
                    <p>{data.address}</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="textBold">{t('email')}*:</p>
                    <p>{data.email}</p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('mobile')}*:</p>
                    <p>{data.mobile}</p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('union')}:</p>
                    {data.union && (
                      <div>
                        <p>{data.union?.name}</p>
                      </div>
                    )}
                    {data.dontKnowUnion && <p>{t('dontKnowUnion')}</p>}
                    {data.hasNoUnion && (
                      <div>
                        <p>{t('hasNoUnion')}</p>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Menntun */}
      <ApplicationINNAEducation />

      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('education')}</h4>
            </Col>
            {(data.status === 'DRAFT' || data.status === 'RETURNED') && (
              <Col>
                {showEducationEdit ? (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('close')}</Tooltip>}>
                    <Button className="float-right" variant="danger" onClick={() => onHandleEditEducation()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('editInformation')}</Tooltip>}>
                    <Button className="float-right" variant="primary" onClick={() => onHandleEditEducation()}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Col>
            )}
          </Row>

          {showEducationEdit ? (
            <ApplicationEducationEdit
              item={data}
              onSaveSuccess={() => {
                onHandleEditEducation();
                getApplication();
              }}
            />
          ) : (
            <Row>
              <Col md="4">
                <p className="small text-muted">{t('educationHelp')}</p>
              </Col>
              <Col md="8">
                {hasEducationError && (
                  <Alert variant="danger" className="mt-2 mb-3">
                    {t('finishCheckboxes')}
                  </Alert>
                )}
                <Row>
                  <Col>
                    <p className="textBold">{t('isMatriculationFinished')}* </p>
                    <p>
                      <Form.Check
                        custom
                        id="isMatriculationFinished-yes"
                        name="isMatriculationFinished"
                        inline
                        disabled
                        label={t('common:yes')}
                        type="checkbox"
                        checked={data.isMatriculationFinished === true}
                      />
                      <Form.Check
                        custom
                        id="isMatriculationFinished-no"
                        name="isMatriculationFinished"
                        inline
                        disabled
                        label={t('common:no')}
                        type="checkbox"
                        checked={data.isMatriculationFinished === false}
                      />
                    </p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('isContractFinished')}* </p>
                    <p>
                      <Form.Check
                        custom
                        id="isContractFinished-yes"
                        name="isContractFinished"
                        inline
                        disabled
                        label={t('common:yes')}
                        type="checkbox"
                        checked={data.isContractFinished === true}
                      />
                      <Form.Check
                        custom
                        id="isContractFinished-no"
                        name="isContractFinished"
                        inline
                        disabled
                        label={t('common:no')}
                        type="checkbox"
                        checked={data.isContractFinished === false}
                      />
                    </p>
                  </Col>
                  <Col>
                    <p className="textBold">{t('hasOngoingContract')}* </p>
                    <p>
                      <Form.Check
                        custom
                        id="hasOngoingContract-yes"
                        name="hasOngoingContract"
                        inline
                        disabled
                        label={t('common:yes')}
                        type="checkbox"
                        checked={data.hasOngoingContract === true}
                      />
                      <Form.Check
                        custom
                        id="hasOngoingContract-no"
                        name="hasOngoingContract"
                        inline
                        disabled
                        label={t('common:no')}
                        type="checkbox"
                        checked={data.hasOngoingContract === false}
                      />
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <p className="textBold">{t('isJourneymanFinished')}* </p>
                    <p>
                      <Form.Check
                        custom
                        id="isJourneymanFinished-yes"
                        name="isJourneymanFinished"
                        inline
                        disabled
                        label={t('common:yes')}
                        type="checkbox"
                        checked={data.isJourneymanFinished === true}
                      />
                      <Form.Check
                        custom
                        id="isJourneymanFinished-no"
                        name="isJourneymanFinished"
                        inline
                        disabled
                        label={t('common:no')}
                        type="checkbox"
                        checked={data.isJourneymanFinished === false}
                      />
                    </p>
                    {data.isJourneymanFinished && (
                      <div>
                        <p className="textBold">{t('ifYes')}:</p>
                        {data.education.map((ei) => (
                          <ul key={ei.id}>
                            <li>{ei.educationIndustry?.name}</li>
                          </ul>
                        ))}
                      </div>
                    )}
                  </Col>
                  <Col>
                    <p className="textBold">{t('isEducationAbroadFinished')}* </p>
                    <p>
                      <Form.Check
                        custom
                        id="isEducationAbroadFinished-yes"
                        name="isEducationAbroadFinished"
                        inline
                        disabled
                        label={t('common:yes')}
                        type="checkbox"
                        checked={data.isEducationAbroadFinished === true}
                      />
                      <Form.Check
                        custom
                        id="isEducationAbroadFinished-no"
                        name="isEducationAbroadFinished"
                        inline
                        disabled
                        label={t('common:no')}
                        type="checkbox"
                        checked={data.isEducationAbroadFinished === false}
                      />
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="textBold">{t('educationOther')}:</p>
                    {data.educationOther?.length === 0 ? (
                      <p>{t('')}</p>
                    ) : (
                      data.educationOther?.map((eo) => (
                        <ul key={eo.id}>
                          <li>{eo.educationOther?.name}</li>
                        </ul>
                      ))
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="textBold">{t('educationCourses')}:</p>
                    {data.course.map((c) => (
                      <ul key={c.id}>
                        <li>{c.name}</li>
                      </ul>
                    ))}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="textBold">{t('otherEducationInformation')}:</p>
                    <p>{data.otherEducationInformation}</p>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      {/* Störf */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('jobs')}</h4>
            </Col>

            {(data.status === 'DRAFT' || data.status === 'RETURNED') && (
              <Col>
                {showJobsEdit ? (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('close')}</Tooltip>}>
                    <Button className="float-right" variant="danger" onClick={() => onHandleEditJobs()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('editInformation')}</Tooltip>}>
                    <Button className="float-right" variant="primary" onClick={() => onHandleEditJobs()}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Col>
            )}
          </Row>

          {showJobsEdit ? (
            <ApplicationJobsEdit
              item={data}
              onSaveSuccess={() => {
                onHandleEditJobs();
                getApplication();
              }}
            />
          ) : (
            <Row>
              <Col md="4">
                <p className="small text-muted">{t('jobHelp')}</p>
              </Col>
              <Col md="8">
                {data.job.map((j) => (
                  <div key={j.id} style={{ paddingBottom: '20px' }}>
                    <Row>
                      <Col>
                        <p className="textBold">{t('job')}:</p>
                        <p>{j.job}</p>
                      </Col>
                      <Col>
                        <p className="textBold">{t('company')}: </p>
                        <p>{j.company}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="textBold">
                          {t('jobDescription')}: <br />
                        </p>
                        <p> {j.description}</p>
                      </Col>
                    </Row>
                    <hr />
                  </div>
                ))}
                {hasJobError && (
                  <Alert variant="danger" className="my-2">
                    {t('oneJobManditory')}
                  </Alert>
                )}
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      {/* Skjöl */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('filesAndImages')}</h4>
            </Col>
            {(data.status === 'DRAFT' || data.status === 'RETURNED') && (
              <Col>
                {showFilesEdit ? (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('close')}</Tooltip>}>
                    <Button className="float-right" variant="danger" onClick={() => onHandleEditFiles()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('editInformation')}</Tooltip>}>
                    <Button className="float-right" variant="primary" onClick={() => onHandleEditFiles()}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Col>
            )}
          </Row>

          {showFilesEdit ? (
            <ApplicationFilesUploadEdit
              item={data}
              onSaveSuccess={() => {
                onHandleEditFiles();
                getApplication();
              }}
            />
          ) : (
            <Row>
              <Col md="4">
                <p className="small text-muted">{t('filesAndImagesHelp')}</p>
              </Col>
              <Col md="8">
                {data.files?.length === 0 ? (
                  <p>{t('noData')}</p>
                ) : (
                  data.files?.map((eo) => (
                    <li key={eo.id}>
                      <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip-other">{t('downloadFile')}</Tooltip>}>
                        <Button variant="link" onClick={() => onDownloadFile(eo.id)}>
                          {eo.name}
                        </Button>
                      </OverlayTrigger>

                      {eo.isPensionfundFile ? (
                        <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('pensionfundFile')}</Tooltip>}>
                          <FontAwesomeIcon icon={faFileAlt} />
                        </OverlayTrigger>
                      ) : (
                        ''
                      )}
                    </li>
                  ))
                )}
                {hasPensionFundFileError && (
                  <Alert variant="danger" className="my-2">
                    {t('onePensionfundFile')}
                  </Alert>
                )}
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      {/* Annað */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('other')}</h4>
            </Col>
            {(data.status === 'DRAFT' || data.status === 'RETURNED') && (
              <Col>
                {showOtherEdit ? (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('close')}</Tooltip>}>
                    <Button className="float-right" variant="danger" onClick={() => onHandleEditOther()}>
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('editInformation')}</Tooltip>}>
                    <Button className="float-right" variant="primary" onClick={() => onHandleEditOther()}>
                      <FontAwesomeIcon icon={faPen} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Col>
            )}
          </Row>
          {showOtherEdit ? (
            <ApplicationOtherEdit
              item={data}
              onSaveSuccess={() => {
                onHandleEditOther();
                getApplication();
              }}
            />
          ) : (
            <Row>
              <Col md="4">
                <p className="small text-muted">{t('otherHelp')}</p>
              </Col>
              <Col md="8">
                <p className="textBold">{t('otherInformation')}:</p>
                <p>{data.otherInformation}</p>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      {/* comments */}
      <Card className="mb-2">
        <Card.Body>
          <Row>
            <Col>
              <h4>{t('comment')}</h4>
            </Col>
          </Row>
          <ApplicationCommentsCreate item={data} />
        </Card.Body>
      </Card>

      <Card className="mb-2">
        <Card.Body className="text-right">
          <Row>
            <Col>
              {(data.status === 'NEW' || data.status === 'RETURNED' || data.status === 'ACCEPTED') && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('cancelApplication')}</Tooltip>}>
                  <Button variant="danger" type="button" onClick={onCancel} className="float-left">
                    {t('cancel')}
                  </Button>
                </OverlayTrigger>
              )}
            </Col>
            <Col>
              {/* <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('saveApplication')}</Tooltip>}>
            <Button variant="primary">{t('common:save')}</Button>
          </OverlayTrigger>{' '} */}
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('closeApplication')}</Tooltip>}>
                <Link to="/" className="btn btn-secondary">
                  {t('close')}
                </Link>
              </OverlayTrigger>{' '}
              {data.status === 'DRAFT' && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('sendApplication')}</Tooltip>}>
                  <Button variant="success" type="button" onClick={onSubmit} disabled={loading}>
                    {t('submit')}
                  </Button>
                </OverlayTrigger>
              )}
              {data.status === 'RETURNED' && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('resubmitApplication')}</Tooltip>}>
                  <Button variant="success" type="button" onClick={onResubmit} disabled={loading}>
                    {t('resubmit')}
                  </Button>
                </OverlayTrigger>
              )}
              {(data.status === 'CANCELLED' || data.status === 'REJECTED') && (
                <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip-other">{t('activateApplication')}</Tooltip>}>
                  <Button variant="success" type="button" onClick={onActivate}>
                    {t('activate')}
                  </Button>
                </OverlayTrigger>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ApplicationView;
